import React, { useCallback } from 'react'

import { PageLayout } from '@components/layouts/PageLayout'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'
import { router } from '@inertiajs/react'
import { callingSessionSupporterUrl } from '../../supporters-management/utils/routes'
import { usePartialPoll } from '@frontend/hooks/usePartialPoll'
import { scopedTranslation } from '@utils/I18n'

import { SupporterReloadProvider } from '../../supporters-management/features/edit-supporter/SupporterReloadContext'

import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { SendSmsDialog } from '../../supporters-management/features/send-sms/SendSmsDialog'
import { LogContactDialog } from '../../supporters-management/features/log-contact/LogContactDialog'
import { ContactPreferencesDialog } from '../../supporters-management/features/update-contact-preferences/ContactPreferencesDialog'
import { SendEmailDialog } from '../../supporters-management/features/send-email/SendEmailDialog'
import { InitiateCallDialog } from '../../supporters-management/features/initiate-call/InitiateCallDialog'
import { InitiateCallingSessionDialog } from '../features/start-automated-calls/InitiateCallingSessionDialog'
import { SkipSupporterDialog } from '../features/skip-supporter/SkipSupporterDialog'
import { SupporterPagination } from '../components/SupporterPagination'
import { hasSupporterId } from '../utils/sessionSupporters'
import { CurrentCycleCard } from '../components/calling-session-dashboard/CurrentCycleCard'
import { SupporterSummaryCard } from '../components/SupporterSummaryCard'
import {
  SupporterTabs,
  type SupporterDetailsType,
} from '@frontend/contexts/supporters-management/components/SupporterTabs'
import type { CallingSessionSupportersShowQueryType } from '@frontend/graphql/queries/calling_sessions/supporters'
import type { SupporterContactsQueryType, SupporterNotesQueryType } from '@frontend/graphql/queries/supporters'
import type { SmsConversationPage } from '@frontend/contexts/supporters-management/features/show-supporter/SmsConversation'
import type { EngagementLevelsIndexQueryType } from '@frontend/graphql/queries/engagementLevels'
import { startDisabledReason } from '../utils/startDisabledReason'

const callingSessionSupporterShowRegistry: DialogRegistry = {
  sendSms: SendSmsDialog,
  logContact: LogContactDialog,
  updateContactPreferences: ContactPreferencesDialog,
  sendEmail: SendEmailDialog,
  initiateCall: InitiateCallDialog,
  initiateCallingSession: InitiateCallingSessionDialog,
  skipSupporter: SkipSupporterDialog,
}

const CALLING_SESSION_POLL_INTERVAL = 3000

type CallingSessionSupporterShowType = {
  callingSessionSupporter: CallingSessionSupportersShowQueryType['callingSessionSupporter']
  contacts?: SupporterContactsQueryType['contacts']
  supporterNotes?: SupporterNotesQueryType['supporterNotes']
  supporterSmsMessages?: SmsConversationPage
  engagementLevels?: EngagementLevelsIndexQueryType['engagementLevels']
}

export type CallingSessionSharedInertiaProps = {
  permissions: {
    updateDetails: boolean
    readTags: boolean
    updateTags: boolean
    updateTagFilters: boolean
    updateEngagementLevel: boolean
    showAddress2: boolean
    showAllTags?: boolean
    electoralFiltersEnabled: boolean
    editFederalDistrict: boolean
    showEmail: boolean
    showPhone: boolean
    canSendIndividualSms?: boolean
    canSendIndividualEmail?: boolean
    smsInboxEnabled?: boolean
    canStartCallingSession?: boolean
    demoSite?: boolean
  }
}

const t = scopedTranslation('calling_sessions.supporter_show')

type CallingSessionSupportersShowType = CallingSessionSupporterShowType & CallingSessionSharedInertiaProps

const CallingSessionSupportersShow = (props: React.PropsWithChildren<CallingSessionSupportersShowType>) => {
  const { callingSessionSupporter, contacts, supporterNotes, supporterSmsMessages, engagementLevels, permissions } =
    props
  const { callingSession, supporter, previousCallingSessionSupporter, nextCallingSessionSupporter } =
    callingSessionSupporter
  const { nextAvailableSessionSupporterToCall, sessionSupporters, totalSupportersCount } = callingSession
  usePartialPoll(CALLING_SESSION_POLL_INTERVAL, ['callingSessionSupporter'], {
    pauseWhenBrowserInactive: true,
  })

  const handleCallingSessionStarted = useCallback(() => {
    const supporterId = nextAvailableSessionSupporterToCall?.supporter?.id
    if (supporterId) {
      router.visit(callingSessionSupporterUrl(callingSession.id, supporterId))
    }
  }, [nextAvailableSessionSupporterToCall?.supporter?.id, callingSession.id])

  return (
    <DialogProvider registry={callingSessionSupporterShowRegistry}>
      <SupporterReloadProvider reloadKeys={['callingSessionSupporter']}>
        <PageLayout.Root>
          <PageLayout.Header icon="user-square" heading={supporter.fullName || t('breadcrumb.supporter')} />
          <PageLayout.Body>
            <div className="tw:mb-5">
              <CurrentCycleCard
                currentCycle={callingSession.currentCycle ?? null}
                availableSupportersCount={callingSession.availableSupportersThisCycleCount}
                callInProgressAt={callingSession.callInProgressAt ?? null}
                callingSessionId={callingSession.id}
                isPaused={callingSession.paused}
                showViewAllButton
                onCallingSessionStarted={handleCallingSessionStarted}
                lastAttemptedSupporter={callingSession.lastAttemptedSessionSupporter?.supporter}
                currentSupporterId={supporter?.id}
                startDisabledReason={startDisabledReason(permissions)}
              />
            </div>
            <AppAlerts alertsPath="supporterDetails" className="tw:mb-4" />

            <SupporterSummaryCard
              callingSessionSupporter={callingSessionSupporter}
              isNextToCall={nextAvailableSessionSupporterToCall?.supporter?.id === supporter?.id}
              showEmail={permissions.showEmail}
              showPhone={permissions.showPhone}
              canSendIndividualSms={permissions.canSendIndividualSms}
              canSendIndividualEmail={permissions.canSendIndividualEmail}
              demoSite={permissions.demoSite}
            />

            {contacts && supporterNotes && engagementLevels && (
              <SupporterTabs
                supporter={supporter satisfies SupporterDetailsType}
                contacts={contacts}
                supporterNotes={supporterNotes}
                supporterSmsMessages={supporterSmsMessages}
                engagementLevels={engagementLevels}
                // TODO: https://github.com/code-nation/action-centre-base/issues/6254
                supporterTagFilters={[]}
                permissions={permissions}
                groupId={callingSessionSupporter.actionGroupId}
                defaultTab="contactsLog"
              />
            )}
          </PageLayout.Body>
          <PageLayout.Footer>
            <SupporterPagination
              currentPosition={callingSessionSupporter.position}
              totalSupporters={totalSupportersCount}
              callingSessionId={callingSession.id}
              sessionSupporters={sessionSupporters.filter(hasSupporterId)}
              only={[
                'breadcrumbs',
                'callingSessionSupporter',
                'contacts',
                'supporterNotes',
                'supporterSmsMessages',
                'engagementLevels',
              ]}
              previousSupporter={previousCallingSessionSupporter as any}
              nextSupporter={nextCallingSessionSupporter as any}
            />
          </PageLayout.Footer>
        </PageLayout.Root>
      </SupporterReloadProvider>
    </DialogProvider>
  )
}

export default CallingSessionSupportersShow
